import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	Button,
	GraphQLErrorMessage,
	Heading3,
	Paragraph,
	Select,
	Switch,
} from '@treams/treams-ui';

import { WorkspaceAdvanced } from './types';

type StyrConfig = {
	organisationID: string;
	organisationLabel: string;
	enabled: boolean;
};

const EMPTY_OPTION_VALUE = '';

// Launch-only admin panel for configuring the Styr enrichment connector on behalf of a
// customer workspace. Mirrors `WorkspaceHubSpot.tsx` in style. Customers cannot self-activate
// Styr; the only customer-facing Styr action is the "Sync now" button on their
// own integrations page once support has activated it from here.
export default function WorkspaceStyr(props: { workspace: WorkspaceAdvanced }) {
	const { t } = useTranslation();

	const query = graphql.useWorkspaceStyrAdminQuery({
		variables: { workspaceID: props.workspace.id },
		skip: !props.workspace.id,
		fetchPolicy: 'network-only',
	});

	const remoteConfig = useMemo<StyrConfig | null>(() => {
		const node = query.data?.workspacesV2?.edges?.find(
			(edge) => edge.node.id === props.workspace.id,
		)?.node;

		const styr = node?.styrConfigAdmin;

		if (!styr) return null;

		return {
			organisationID: styr.organisationID,
			organisationLabel: styr.organisationLabel,
			enabled: styr.enabled,
		};
	}, [query.data, props.workspace.id]);

	const [styrConfig, setStyrConfig] = useState<StyrConfig>({
		organisationID: remoteConfig?.organisationID ?? '',
		organisationLabel: remoteConfig?.organisationLabel ?? '',
		enabled: remoteConfig?.enabled ?? false,
	});

	const updateStyrConfig = useCallback((patch: Partial<StyrConfig>) => {
		setStyrConfig((prev) => ({ ...prev, ...patch }));
	}, []);

	// Reset the styrConfig whenever the persisted config changes (initial load / workspace switch).
	useEffect(() => {
		setStyrConfig({
			organisationID: remoteConfig?.organisationID ?? '',
			organisationLabel: remoteConfig?.organisationLabel ?? '',
			enabled: remoteConfig?.enabled ?? false,
		});
	}, [remoteConfig?.organisationID, remoteConfig?.organisationLabel, remoteConfig?.enabled]);

	const organisationsQuery = graphql.useStyrOrganisationsQuery({
		fetchPolicy: 'cache-and-network',
	});

	const organisations = useMemo(
		() => organisationsQuery.data?.styrOrganisations ?? [],
		[organisationsQuery.data],
	);

	// Hydrate the label for legacy connector rows that only stored the ID. The first
	// time the list loads we can look the saved ID up by name and back-fill the label so
	// the next save round-trips both fields.
	useEffect(() => {
		if (styrConfig.organisationLabel || !styrConfig.organisationID || organisations.length === 0) {
			return;
		}

		const match = organisations.find(
			(organisation) => organisation.id === styrConfig.organisationID,
		);

		if (match) {
			updateStyrConfig({ organisationLabel: match.name });
		}
	}, [organisations, styrConfig.organisationID, styrConfig.organisationLabel, updateStyrConfig]);

	const options = useMemo(
		() => [
			{
				value: EMPTY_OPTION_VALUE,
				title: t('settings.workspaces.styr.noOrganisation'),
			},
			...organisations.map((o) => ({ value: o.id, title: o.name })),
		],
		[organisations, t],
	);

	const selectedOption = useMemo(() => {
		if (!styrConfig.organisationID) {
			return {
				value: EMPTY_OPTION_VALUE,
				title: t('settings.workspaces.styr.noOrganisation'),
			};
		}
		return {
			value: styrConfig.organisationID,
			title: styrConfig.organisationLabel || styrConfig.organisationID,
		};
	}, [styrConfig.organisationID, styrConfig.organisationLabel, t]);

	// The shared `Select` only forwards the selected value (not the option), so resolve the label
	// from the loaded organisation list rather than relying on a second callback argument.
	const onChangeOrganisation = useCallback(
		(value?: string | number) => {
			const nextID = typeof value === 'string' ? value : String(value ?? '');
			const label = nextID
				? (organisations.find((organisation) => organisation.id === nextID)?.name ?? '')
				: '';
			setStyrConfig((prev) => ({
				...prev,
				organisationID: nextID,
				organisationLabel: label,
				enabled: nextID ? prev.enabled : false,
			}));
		},
		[organisations],
	);

	const [save, { loading, error }] = graphql.useSetWorkspaceStyrConfigMutation();

	const hasStyrOrganisationID = !!styrConfig.organisationID.trim();

	const onSave = useCallback(async () => {
		try {
			await save({
				variables: {
					input: {
						workspaceID: props.workspace.id,
						organisationID: styrConfig.organisationID.trim(),
						organisationLabel: styrConfig.organisationLabel.trim(),
						enabled: hasStyrOrganisationID ? styrConfig.enabled : false,
					},
				},
			});
		} catch {
			// Error is exposed via the mutation hook below.
		}
	}, [save, props.workspace.id, styrConfig, hasStyrOrganisationID]);

	const dirty =
		styrConfig.organisationID.trim() !== (remoteConfig?.organisationID ?? '') ||
		styrConfig.organisationLabel.trim() !== (remoteConfig?.organisationLabel ?? '') ||
		styrConfig.enabled !== (remoteConfig?.enabled ?? false);

	const canSave = !loading && dirty;

	return (
		<Box flexGrow={1} flexBasis={0} mt="30px">
			<Heading3 mb={2}>{t('settings.workspaces.styr.label')}</Heading3>
			<Paragraph mb={2} small>
				{t('settings.workspaces.styr.description')}
			</Paragraph>
			{error && (
				<Box mb={2}>
					<GraphQLErrorMessage error={error.graphQLErrors?.[0] ?? error} />
				</Box>
			)}
			<Box mb={2}>
				<Select
					options={options}
					selectedOption={selectedOption}
					selectedValue={styrConfig.organisationID || EMPTY_OPTION_VALUE}
					onChange={onChangeOrganisation}
					fetch={() => organisationsQuery.refetch()}
					placeholder={t('settings.workspaces.styr.selectOrganisation')}
				/>
				{organisationsQuery.error && (
					<Box mt={1}>
						<GraphQLErrorMessage
							error={organisationsQuery.error.graphQLErrors?.[0] ?? organisationsQuery.error}
						/>
					</Box>
				)}
			</Box>
			<Flex alignItems="center" mb={2}>
				<Box flexGrow={1}>
					<Paragraph>{t('settings.workspaces.styr.isActive')}</Paragraph>
					{!hasStyrOrganisationID && (
						<Paragraph small color="grey">
							{t('settings.workspaces.styr.isActive.requiresOrganisationID')}
						</Paragraph>
					)}
				</Box>
				<Switch
					on={styrConfig.enabled}
					onChange={(on: boolean) => updateStyrConfig({ enabled: on })}
					disabled={!hasStyrOrganisationID}
				/>
			</Flex>
			<Flex justifyContent="flex-end">
				<Button
					small
					type="primary"
					title={t('common.save')}
					disabled={!canSave}
					onClick={onSave}
				/>
			</Flex>
		</Box>
	);
}
